import Chip from '@mui/material/Chip'
import _ from 'lodash'
import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import styles from './ProductFilterChips.module.scss'
import { ActiveFiltersInterface, ProductFiltersInterface } from '../../../interfaces/FiltersInterface'
import { ProductSizeInterface } from '../../../interfaces/ProductInterface'
import { DEFAULT_LANGUAGE_ISO_CODE } from '../../../utils/locale'
import { FILTER_FLAT, FILTER_RANGE, isAttributeChecked, isFlatAttributeChecked } from '../../../utils/productFilter'
import { _at } from '../../../utils/translations'

interface Props {
  filters: ProductFiltersInterface
  activeFilters: ActiveFiltersInterface
  onRangeChanged: (attributeId: string, minValue: string | null, maxValue: string | null, extra?: object) => void
  onAttributeChange: (attributeId: number, valueId: number, checked: boolean) => void
  onFlatAttributeChange: (name: string, valueId: number | string, checked: boolean) => void
}

type RangeChipType = 'price'

const ProductFilterChips: React.FC<Props> = (props: Props) => {
  const [activeFilters, setActiveFilters] = useState<ActiveFiltersInterface>(props.activeFilters)
  const flatAttributeChipsHtml = Object.values(FILTER_FLAT)
  const rangeChipsHtml = Object.values(FILTER_RANGE)
  const intl = useIntl()

  useEffect(() => {
    setActiveFilters(props.activeFilters)
  }, [JSON.stringify(props.activeFilters)])

  const onAttributeValueDelete = (attributeId: number, valueId: number, currentValueIds: number[]) => {
    const _activeFilters = _.cloneDeep(activeFilters)
    _activeFilters.attributes![attributeId] = {}
    for (const value of currentValueIds) {
      if (value === valueId) {
        continue
      }
      _activeFilters.attributes![attributeId][value] = '1'
    }
    setActiveFilters(_activeFilters)
    props.onAttributeChange(attributeId, valueId, false)
  }

  const onFlatAttributeValueDelete = (enumItem: string, value: string | number) => {
    const activeName = (enumItem === FILTER_FLAT.SIZES ? 'sizeKeys' : enumItem) as keyof ActiveFiltersInterface
    const _activeFilters = _.cloneDeep(activeFilters)
    ;(_activeFilters[activeName] as (string | number)[]) = (
      activeFilters[activeName as keyof ActiveFiltersInterface] as (string | number)[]
    ).filter(id => id !== value)
    setActiveFilters(_activeFilters)
    props.onFlatAttributeChange(enumItem, value, false)
  }

  const onRangeValueDelete = (enumItem: RangeChipType, removeValue: { min?: boolean; max?: boolean; saleItems?: boolean }) => {
    if (!activeFilters.price) {
      return
    }
    const _activeFilters = _.cloneDeep(activeFilters)
    if (removeValue.min) {
      delete _activeFilters.price!.min
    }
    if (removeValue.max) {
      delete _activeFilters.price!.max
    }
    if (removeValue.saleItems) {
      delete _activeFilters.price!.saleItems
    }
    setActiveFilters(_activeFilters)
    props.onRangeChanged(enumItem, _activeFilters.price?.min || null, _activeFilters.price?.max || null, {
      saleItems: _activeFilters.price?.saleItems,
    })
  }

  const renderRangeChip = (enumItem: RangeChipType) => {
    const minLabel = intl.formatMessage({ id: `Filters.Label.Flat.${_.upperFirst(enumItem)}.Min` })
    const maxLabel = intl.formatMessage({ id: `Filters.Label.Flat.${_.upperFirst(enumItem)}.Max` })
    const saleItemsLabel = intl.formatMessage({ id: `Filters.Label.Flat.${_.upperFirst(enumItem)}.SaleItems` })
    const range = activeFilters[enumItem]
    return (
      <React.Fragment key={`range_${enumItem}`}>
        {range?.min !== null && Number(range?.min) >= 0 ? (
          <Chip
            label={`${minLabel}: ${range?.min}`}
            onDelete={() => onRangeValueDelete(enumItem, { min: true })}
            key={`min_${enumItem}`}
            className={styles['product-filters__chip']}
          />
        ) : null}
        {range?.max !== null && Number(range?.max) >= 0 ? (
          <Chip
            label={`${maxLabel}: ${range?.max}`}
            onDelete={() => onRangeValueDelete(enumItem, { max: true })}
            key={`max_${enumItem}`}
            className={styles['product-filters__chip']}
          />
        ) : null}
        {range?.saleItems ? (
          <Chip
            label={saleItemsLabel}
            onDelete={() => onRangeValueDelete(enumItem, { saleItems: true })}
            key={`saleItems_${enumItem}`}
            className={styles['product-filters__chip']}
          />
        ) : null}
      </React.Fragment>
    )
  }

  const rangeChipsHtmlElement = rangeChipsHtml.map(enumItem => props.filters[enumItem] && renderRangeChip(enumItem)).filter(Boolean)

  const attributeChipsHtml = props.filters.attributes?.map(attribute => {
    const activeValues = activeFilters.attributes?.[attribute.id] || {}
    const valueIds: number[] = []
    for (const key in activeValues) {
      if (+activeValues[key]) {
        valueIds.push(+key)
      }
    }
    return attribute?.values
      .filter(value => isAttributeChecked(activeFilters, attribute.id, value.id))
      .map(value => {
        const label = value.translatable ? _at('value', value.translations, DEFAULT_LANGUAGE_ISO_CODE) : value.stringValue
        return (
          <Chip
            label={label}
            onDelete={() => onAttributeValueDelete(attribute.id, value.id, valueIds)}
            key={`${attribute.id}_${value.id}`}
            className={styles['product-filters__chip']}
          />
        )
      })
  })

  const flatAttributeChipsHtmlElement = flatAttributeChipsHtml.map(enumItem => {
    return props.filters[enumItem]
      ?.filter((attr: ProductSizeInterface) => {
        const value = attr.hasOwnProperty('sizeKey') ? attr.sizeKey : attr.id
        const name = enumItem === FILTER_FLAT.SIZES ? 'sizeKeys' : enumItem
        return isFlatAttributeChecked(activeFilters, name, value)
      })
      .map((attr: ProductSizeInterface) => {
        const label = intl.formatMessage({ id: `Filters.Label.Flat.${_.upperFirst(enumItem as string)}.Label` })
        const value = attr.hasOwnProperty('sizeKey') ? attr.sizeKey : attr.id
        return (
          <Chip
            label={`${label}: ${attr.label}`}
            onDelete={() => onFlatAttributeValueDelete(enumItem, value)}
            key={`${attr.id}_${enumItem}`}
            className={styles['product-filters__chip']}
          />
        )
      })
  })

  return (
    <div className={styles['product-filters__chips']}>
      {attributeChipsHtml}
      {flatAttributeChipsHtmlElement}
      {rangeChipsHtmlElement}
    </div>
  )
}

export default ProductFilterChips
